import React from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { fetchManualTimePeople, type TimePerson } from '@/data/scheduleTimeTracking';
import SearchSelect from '../form/SearchSelect';
import { safeErrorMessage } from '@/utils/appErrors';
import { useDebouncedSearch } from './useDebouncedSearch';

/** Employee field for recording someone's time: type a name, then choose from the people this admin may manage. */
export default function TimeEmployeePicker({ id, locationId, authorizationKey, value, onChange, disabled, invalid, describedBy, inputClassName, inputRef, children }: {
  id: string; locationId: string; authorizationKey: string; value: TimePerson | null; onChange: (person: TimePerson) => void;
  disabled?: boolean; invalid?: boolean; describedBy?: string; inputClassName?: string; inputRef?: React.Ref<HTMLInputElement>; children?: React.ReactNode;
}) {
  const [search, setSearch] = React.useState('');
  const term = useDebouncedSearch(search);
  const people = useInfiniteQuery({
    queryKey: ['schedule-time-manual-people', authorizationKey, locationId, term], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchManualTimePeople(locationId, term, pageParam),
    getNextPageParam: page => page.has_more ? page.people[page.people.length - 1]?.id : undefined,
    placeholderData: keepPreviousData,
  });
  const options = (people.data?.pages.flatMap(page => page.people) ?? []).map(person => ({ id: person.id, label: person.name }));
  return <SearchSelect id={id} label="Employee" value={value ? { id: value.id, label: value.name } : null} options={options}
    search={search} onSearchChange={setSearch} onChange={option => onChange({ id: option.id, name: option.label })}
    placeholder="Type a name" loading={people.isPending || people.isPlaceholderData || term !== search.trim()}
    error={people.isError ? safeErrorMessage(people.error, 'Employees could not be loaded.') : undefined} onRetry={() => { void people.refetch(); }}
    emptyText="No matching employees you can manage." hasMore={people.hasNextPage} loadingMore={people.isFetchingNextPage}
    onLoadMore={() => { void people.fetchNextPage(); }} loadMoreLabel="Load more employees" required
    disabled={disabled} invalid={invalid} describedBy={describedBy} inputClassName={inputClassName} inputRef={inputRef}>{children}</SearchSelect>;
}
